<template>
  <Modal
    ref="customizeTableModalRef"
    @onPositiveActionClick="handlePositiveActionClick"
    @onNegativeActionClick="customizeTableModalRef?.hiddenModal()"
    @modalClosed="customizeTableCancel"
    positiveActionText="Kaydet"
    negativeActionText="Vazgeç"
    class="customizeTableModal"
    :withCloser="true"
    :showIcon="false"
  >
    <template v-slot:body>
      <div class="customizeTableModal-context">
        <h2 class="customizeTableModal-title">Tabloyu Özelleştir</h2>
        <p>
          Görüntülemek istediğiniz sütunları seçebilir, sürükleyip bırakarak sütun sıralamasını özelleştirebilirsiniz.
        </p>
        <div>
          <div class="customizeTableModal-container">
            <Checkbox
              label="Tümünü Seç"
              key="select-all"
              id="select-all"
              class="customizeTableModal-checkbox"
              :labelStyle="checkboxLabelStyle"
              :checked="tableRef.options.meta.getIsAllColumnsVisible()"
              :onClickFn="() => tableRef.options.meta.toggleAllColumnsVisibility()"
            />
            <a href="#" @click="handleColumnVisibility" v-if="hasVisibleDetachableColumns">
              Seçilenleri Kaldır (<span v-html="hasVisibleDetachableColumns"></span>)
            </a>
          </div>
          <div class="customizeTableModal-sortableArea-wrapper" v-if="customizableColumns">
            <Sortable
              :list="customizableColumns"
              item-key="key"
              @end="(event) => tableRef.options.meta.orderColumns(event)"
              tag="div"
              :options="{
                draggable: '.customizeTableModal-row',
                animation: 150,
                ghostClass: 'ghost',
                dragClass: 'sortable-drag',
                handle: '.customizeTableModal-dnd_handler',
                group: 'testgroup',
                scroll: true,
                forceFallback: true,
                bubbleScroll: true,
              }"
            >
              <template #item="{ element, index }">
                <div class="customizeTableModal-row">
                  <div class="customizeTableModal-row-wrapper">
                    <Checkbox
                      :key="`${element.key}-${element.order}`"
                      :checkBoxDisabled="!element.isDetachable"
                      :id="`colum-${element.key}`"
                      :label="element.title"
                      class="customizeTableModal-checkbox"
                      :labelStyle="checkboxLabelStyle"
                      :checked="element.isVisible"
                      :onClickFn="() => tableRef.options.meta.toggleColumnVisibility(element)"
                    />
                    <span class="customizeTableModal-dnd_handler">
                      <Handler />
                    </span>
                  </div>
                </div>
              </template>
            </Sortable>
          </div>
        </div>
      </div>
    </template>
  </Modal>
</template>
<script setup>
import { ref, computed } from 'vue';
import { Sortable } from "sortablejs-vue3";
import { Modal, Checkbox } from "@n11/so-ui-components"
import Handler from '@/components/Table/Handler.vue'

const props = defineProps({
  productsTableRef: {
    type: Object,
    required: false,
    default: {},
  }
})

const checkboxLabelStyle = {
  fontSize: '14px',
  lineHeight: '18px',
  marginLeft: '6px',
};

const customizeTableModalRef = ref();

const tableRef = computed(() => props.productsTableRef?.table);
const customizableColumns = computed(() => props.productsTableRef?.customizedColumns);
const hasVisibleDetachableColumns = computed(() => customizableColumns.value.filter((c) => c.isDetachable && c.isVisible).length);

const openCustomizeTableModal = () => customizeTableModalRef?.value?.openModal();

const closeCustomizeTableModal = () => {
  customizeTableModalRef.value?.hiddenModal();
}

const customizeTableCancel = () => tableRef.value.options.meta.setStatusOfCheckboxLocalStorage();

const handleColumnVisibility = (e) => tableRef.value.options.meta.resetAllColumnVisibility(e);

const handlePositiveActionClick = () => {
  if (tableRef.value) {
    tableRef.value.options.meta.submitCustomizedColumns();
  }
  closeCustomizeTableModal();
};

defineExpose({
  openCustomizeTableModal,
})
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables.scss";
@import "@/assets/styles/utilities.scss";

.customizeTableModal {
  .draggable {
    background: $colorN00;
    padding: 10px;
    margin: 10px;
    border: 1px solid $colorD30;
    cursor: move;
  }

  .ghost {
    .customizeTableModal-row-wrapper {
     border: 0.5px solid $colorN30;
     opacity: 0.5;
     color: $colorD40;
     background: $colorN00;
    }

    .checkbox-content {
      opacity: 0.5;
    }
  }

  .drag {
    background: $colorN10;
  }

  .sortable-drag {
    border: 0.5px solid $colorM50;
    opacity: 1 !important;

    .customizeTableModal-row-wrapper {
      border: 3px solid $colorM50;
      background: $colorM05;
    }
  }

  &-sortableArea-wrapper {
    max-height: 440px;
    overflow-y: scroll;
    padding-right: 4px;

    &>div {
      display: flex;
      flex-direction: column;
      gap: 12px;
    }
  }

  .customizeTableModal-sortableArea-wrapper::-webkit-scrollbar {
    width: 6px;
    background-color: $colorN00;
  }

  .customizeTableModal-sortableArea-wrapper::-webkit-scrollbar-thumb {
    width: 4px;
    border-radius: 8px;
    background-color: $colorM50;
  }

  &-title {
    font-size: 20px;
    font-weight: 700;
    text-align: left;
    line-height: 26px;
    margin-bottom: 0;
  }

  &-context {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    gap: 16px;
    padding-bottom: 16px;
    max-height: 640px;

    p {
      line-height: 24px;
      letter-spacing: -0.2px;
      text-align: left;
      user-select: none;

      b {
        font-weight: bold;
      }
    }

    h2 {
      user-select: none;
    }
  }

  &-checkbox {
    display: inline-block;
  }

  &-container {
    display: flex;
    justify-content: space-between;
    margin-bottom: 12px;
  }

  &-container a {
    color: $colorM50;
    font-size: 14px;
    user-select: none;
  }

  &-wrapperRow {
    border: 1px solid transparent;
  }

  &-row {
    border-radius: 6px;
    border: 0.5px solid $colorN30;
    overflow: hidden;

    &-wrapper {
      margin: -1px;
      background-color: $colorN00;
      padding: 8px 12px 8px 16px;
      border: 3px solid transparent;
      display: flex;
      justify-content: space-between;
      align-items: center;
      border-radius: 7px;
      transition: all 150ms ease-in-out;

      svg {
        color: $colorD30;
        transition: all 150ms ease-in-out;
      }

      .checkbox-content input {
        margin: 0;
      }
    }
  }

  &-dnd_handler {
    display: inline-flex;
    cursor: move;

    .sortable-drag-icon {
      transition: all 150ms ease-in-out;
      width: 24px;
      height: 24px;
    }
  }
}
</style>
